/** @format */

import React, { useEffect, useState, useRef, useContext } from 'react';
import { flushSync } from 'react-dom';
import Page from 'src/components/Page';
import { Button, WidgetGrid } from '../../components';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'src/store';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { getDashboard, setCurrentUserDashboard } from 'src/slices/users';
import { selectDashboardById } from 'src/slices/dashboards';
import { DashboardSkeleton } from 'src/components/dashboard/DashboardSkeleton';
import { Box } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { DialogContext } from 'src/contexts/DialogContext';
import OnBoardingView from '../onboarding/OnBoardingView';
import Skeleton from '@material-ui/lab/Skeleton';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

function Dashboard() {
  const { open, setOpen } = useContext(DialogContext);
  const history = useHistory();
  const { t } = useTranslation();
  const { dashboardId } = useParams();
  const dispatch = useDispatch();
  const { dashboardLoading, dashboardError, dashboardInfo } = useSelector(state => state.users);
  const cachedDashboard = useSelector(selectDashboardById(dashboardId));

  const [displayedContent, setDisplayedContent] = useState({ loading: !cachedDashboard, info: cachedDashboard || null });
  const prevLoadingRef = useRef(dashboardLoading);

  useEffect(() => {
    dispatch(getDashboard({ dashboardId }));
    dispatch(setCurrentUserDashboard(Number(dashboardId)));
  }, [dashboardId, dispatch]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = dashboardLoading;

    if (wasLoading && !dashboardLoading) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, info: dashboardInfo });
      });
    } else if (dashboardLoading && !wasLoading) {
      // Show cached dashboard from store instead of skeleton while refetching
      setDisplayedContent(cachedDashboard ? { loading: false, info: cachedDashboard } : { loading: true, info: null });
    } else if (!dashboardLoading) {
      setDisplayedContent({ loading: false, info: dashboardInfo });
    }
  }, [dashboardLoading, dashboardInfo, cachedDashboard]);

  const handleBackToMainDashboard = () => {
    dispatch(setCurrentUserDashboard(null));
    history.push(`/dashboard`);
  };

  if (dashboardError) return <Redirect to="/404" />;
  return (
    <Page className={`dashboard`} title="Dashboard">
      <Breadcrumb
        data={[
          {
            title: t('dashboard'),
            link: '/dashboard'
          },
          {
            title: dashboardInfo?.currentUserDashboard?.[dashboardId] || (
              <Skeleton variant="text" width={100} height={20} style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />
            )
          }
        ]}
      />
      <Box style={{ viewTransitionName: 'dashboard-grid' }}>
        {displayedContent.loading ? <DashboardSkeleton /> : <WidgetGrid dashboardInfo={displayedContent.info} />}
      </Box>

      <Box className="container back-button-container">
        <Button borderOnly back onClick={handleBackToMainDashboard}>
          {t('backPreviousPage')}
        </Button>
      </Box>

      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') setOpen(false);
        }}
        PaperProps={{
          style: { borderRadius: 10, maxWidth: 1110 }
        }}
        aria-labelledby="onboarding-dialog"
      >
        <DialogContent style={{ padding: 0, overflow: 'hidden' }}>
          <OnBoardingView isModal onCloseModal={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </Page>
  );
}

export default Dashboard;
